import React, { useEffect, useState } from 'react';
import { HomeLayouts } from '../../../components/layouts/HomeLayouts';
import Loading from '../../../components/Loading';
import { useNavigate, useParams } from 'react-router-dom';
import { IoArrowBackOutline as Back } from '@react-icons/all-files/io5/IoArrowBackOutline';
import { useSelector } from 'react-redux';
import { quoteForId, quoteUpdate, quoteNoteUpdate } from '../../../api/quotes';
import moment from 'moment';
import PopUp from '../../../components/PopUp';
import CancelQuote from '../../../components/PopUp/components/CancelQuote/CancelQuote';
import { getDisplayDate } from '../../../utils/dates';
import toast from 'react-hot-toast';
import styles from './quoteDetail.module.scss';

// ── Product image with green letter fallback ─────────────────────
const ProductImage = ({ image, name, onExpand }) => {
  const [imgError, setImgError] = useState(false);
  const initial = name ? name.charAt(0).toUpperCase() : '?';
  const isImage = image && !imgError;

  const handleClick = () => {
    if (onExpand) onExpand({ isImage, src: image, name, initial });
  };

  if (isImage) {
    return (
      <div className={styles.productImageWrap} onClick={handleClick}>
        <img
          src={image}
          alt={name}
          className={styles.productImage}
          onError={() => setImgError(true)}
        />
      </div>
    );
  }
  return (
    <div className={styles.productImageWrap} onClick={handleClick}>
      <div className={styles.productPlaceholder}>{initial}</div>
    </div>
  );
};

export const QuotesDetail = () => {
  const { token } = useSelector((state) => state.auth);
  const { id } = useParams();
  const [order, setorder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setloading] = useState(false);
  const [loadingSend, setloadingSend] = useState(false);
  const [clientNote, setClientNote] = useState('');
  const [originalNote, setOriginalNote] = useState('');
  const [expandedImage, setExpandedImage] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadQuote();
  }, []);

  const loadQuote = async () => {
    setloading(true);
    const resp = await quoteForId(token, id);
    if (resp.ok) {
      setorder(resp.data);
      const note = resp.data.note ?? '';
      setClientNote(note);
      setOriginalNote(note);
    } else {
      setorder(null);
    }
    setloading(false);
  };

  const submit = async (status) => {
    setloadingSend(true);
    const data = {
      status,
      note: clientNote,
      products: [
        {
          supplierId: order.supplierId.toString(),
          restaurantId: order.restaurantId.toString(),
          productId: order.products[0].productRelatedId.toString(),
          price: parseFloat(order.products[0].price),
        },
      ],
    };

    if (status === 'Aceptada') {
      const resp = await quoteUpdate(token, id, data);
      if (resp.ok) navigate(`/cotizaciones/confirmacion_cotizacion/${order.code}`);
      setloadingSend(false);
    } else {
      const resp = await quoteUpdate(token, id, data);
      if (resp.ok) {
        toast.success('Cotización rechazada correctamente');
        loadQuote();
      }
      setloadingSend(false);
    }
  };

  const handleSaveNote = async () => {
    try {
      const resp = await quoteNoteUpdate(token, id, { note: clientNote });
      if (resp.ok) {
        toast.success('Nota guardada correctamente');
        setOriginalNote(clientNote);
      } else {
        toast.error('Error al guardar la nota');
      }
    } catch {
      toast.error('Error al guardar la nota');
    }
  };

  // Client can edit note when quote is Solicitada (waiting for supplier)
  const noteEditable = order && order.status === 'Solicitada';
  // Show price when supplier has already responded
  const showPrice = order && (order.status === 'Completada' || order.status === 'Aceptada');
  // Actions only when supplier has completed the quote (waiting for client decision)
  const showActions = order && order.status === 'Completada';
  // Show supplier contact only when quote is completed or accepted
  const showContact = order && (order.status === 'Completada' || order.status === 'Aceptada');

  return (
    <HomeLayouts>
      {loading || order === null ? (
        <Loading />
      ) : (
        <>
          {/* Image lightbox */}
          {expandedImage && (
            <div className={styles.lightbox} onClick={() => setExpandedImage(null)}>
              {expandedImage.isImage ? (
                <img
                  src={expandedImage.src}
                  alt={expandedImage.name}
                  className={styles.lightboxImg}
                />
              ) : (
                <div className={styles.lightboxPlaceholder}>
                  {expandedImage.initial}
                </div>
              )}
            </div>
          )}

          <PopUp setShowModal={setShowModal} showModal={showModal}>
            <CancelQuote
              setShowModal={setShowModal}
              comerc={order.nameSupplier}
              suppliuer={false}
              date={getDisplayDate(order.createdAt)}
              onClick={() => submit('Rechazada')}
            />
          </PopUp>

          <div className={styles.page}>
            <div className={styles.pageInner}>

              {/* ────────────── LEFT PANEL ────────────── */}
              <div className={styles.leftPanel}>

                <div className={styles.backRow} onClick={() => navigate('/cotizaciones')}>
                  <Back className={styles.backIcon} />
                  <span>Volver</span>
                </div>

                <div className={styles.titleBlock}>
                  <h1 className={styles.pageTitle}>Detalle de cotización</h1>
                  <p className={styles.pageSubtitle}>
                    De{' '}
                    <span className={styles.pageSubtitleHighlight}>
                      {order.nameSupplier}
                    </span>
                  </p>
                </div>

                {/* Products */}
                <div className={styles.sectionCard}>
                  <p className={styles.sectionLabel}>Productos cotizados</p>

                  <div className={`${styles.productsHeader} ${showPrice ? styles.withPrice : ''}`}>
                    <span className={styles.colHeader}>Producto</span>
                    <span className={styles.colHeader}>Cantidad</span>
                    {showPrice && (
                      <span className={styles.colHeader}>Precio</span>
                    )}
                  </div>

                  {order.products.map((product, index) => (
                    <div
                      key={index}
                      className={`${styles.productRow} ${showPrice ? styles.withPrice : ''}`}
                    >
                      <div className={styles.productInfo}>
                        <ProductImage
                          image={product.image}
                          name={product.name}
                          onExpand={(data) => setExpandedImage(data)}
                        />
                        <span className={styles.productName}>{product.name}</span>
                      </div>

                      <span className={styles.productQty}>
                        {product.quantity} {product.unitOfMeasure}
                      </span>

                      {showPrice && (
                        <span className={styles.productPrice}>
                          ${product.price} / {product.unitOfMeasure}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Client note (editable when Solicitada) */}
                <div className={styles.sectionCard}>
                  <div className={styles.noteLabelRow}>
                    <span className={styles.noteLabel}>Tu nota</span>
                    {clientNote !== originalNote && noteEditable && (
                      <button className={styles.saveNoteBtn} onClick={handleSaveNote}>
                        Guardar
                      </button>
                    )}
                  </div>
                  <textarea
                    className={noteEditable ? styles.noteEditable : styles.noteReadOnly}
                    onChange={(e) => setClientNote(e.target.value)}
                    value={clientNote}
                    readOnly={!noteEditable}
                    placeholder={noteEditable ? 'Escribe una nota para el proveedor...' : 'Sin notas del cliente'}
                  />
                </div>

                {/* Supplier note (always read-only for client) */}
                <div className={styles.sectionCard}>
                  <div className={styles.noteLabelRow}>
                    <span className={styles.noteLabel}>Nota del proveedor</span>
                  </div>
                  <textarea
                    className={styles.noteReadOnly}
                    value={order.noteSupplier || ''}
                    readOnly
                    placeholder="Sin notas del proveedor"
                  />
                </div>

              </div>

              {/* ────────────── RIGHT PANEL (DARK SIDEBAR) ────────────── */}
              <div className={styles.rightPanel}>

                <div className={styles.statusBlock}>
                  <p className={styles.sidebarSectionLabel}>Estado</p>
                  <span className={`${styles.statusPillLarge} ${styles[order.status]}`}>
                    {order.status}
                  </span>
                </div>

                <div className={styles.sidebarDivider} />

                <div className={styles.sidebarSection}>
                  <p className={styles.sidebarSectionLabel}>Resumen</p>

                  <div className={styles.summaryRow}>
                    <span className={styles.summaryKey}>Código</span>
                    <span className={`${styles.summaryValue} ${styles.summaryCode}`}>
                      {order.code}
                    </span>
                  </div>

                  <div className={styles.summaryRow}>
                    <span className={styles.summaryKey}>Proveedor</span>
                    <span className={styles.summaryValue}>
                      {order.nameSupplier}
                    </span>
                  </div>

                  <div className={styles.summaryRow}>
                    <span className={styles.summaryKey}>Fecha</span>
                    <span className={styles.summaryValue}>
                      {moment(order.createdAt).format('DD MMM YYYY')}
                    </span>
                  </div>

                  {order.responseDate && (
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryKey}>Respondida</span>
                      <span className={styles.summaryValue}>
                        {moment(order.responseDate).format('DD MMM YYYY')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Supplier contact — visible only when Completada or Aceptada */}
                {showContact && (order.supplierContactName || order.supplierPhone) && (
                  <>
                    <div className={styles.sidebarDivider} />
                    <div className={styles.sidebarSection}>
                      <p className={styles.sidebarSectionLabel}>Contacto del proveedor</p>

                      {order.supplierContactName && (
                        <div className={styles.summaryRow}>
                          <span className={styles.summaryKey}>Nombre</span>
                          <span className={styles.summaryValue}>{order.supplierContactName}</span>
                        </div>
                      )}

                      {order.supplierPhone && (
                        <div className={styles.summaryRow}>
                          <span className={styles.summaryKey}>Teléfono</span>
                          <span className={styles.summaryValue}>{order.supplierPhone}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Actions — only when supplier has completed the quote */}
                {showActions && (
                  <div className={styles.actionsWrap}>
                    <button
                      className={styles.btnPrimary}
                      disabled={loadingSend}
                      onClick={() => submit('Aceptada')}
                    >
                      Aceptar cotización
                    </button>

                    <button
                      className={styles.btnDanger}
                      disabled={loadingSend}
                      onClick={() => setShowModal(true)}
                    >
                      Rechazar cotización
                    </button>
                  </div>
                )}

              </div>
            </div>
          </div>
        </>
      )}
    </HomeLayouts>
  );
};
